import debounce from 'OK/utils/debounce';
import * as webapi from 'OK/webapi';

const DEBOUNCE_DELAY = 1000;
const RESOURCE_NAME_PATTERN: RegExp = /^https?:\/\/([\S]+)\/res\/[\S]+$/;
const IMAGE_NAME_PATTERN: RegExp = /^https?:\/\/([\S]+)\/i\?r=[\S]+$/;
const DELIVERY_TYPE_FIELD: string = 'deliveryType';

interface OkPerformanceResourceTiming {
    stateId: string;
    deliveryType: string;
    initiatorType: string;
    encodedBodySize: number;
    decodedBodySize: number;
    renderBlockingStatus: string;
    responseStatus: number;
    name: string;
    startTime: number;
    entryType: string;
    duration: number;
    protocol: string;
}

type TPerformanceResourceTimingKeys = keyof PerformanceResourceTiming;

function convert(entry: PerformanceResourceTiming, stateId: string): OkPerformanceResourceTiming {
    return {
        stateId: stateId,
        deliveryType: entry[DELIVERY_TYPE_FIELD as TPerformanceResourceTimingKeys],
        initiatorType: entry.initiatorType,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize,
        renderBlockingStatus: entry['renderBlockingStatus' as TPerformanceResourceTimingKeys],
        responseStatus: entry['responseStatus' as TPerformanceResourceTimingKeys],
        name: entry.name,
        startTime: entry.startTime,
        entryType: entry.entryType,
        duration: entry.duration,
        protocol: entry.nextHopProtocol
    } as unknown as OkPerformanceResourceTiming;
}

class PerformanceTimingLogger {
    private observer: PerformanceObserver | null = null;
    private entries: OkPerformanceResourceTiming[] = [];

    private logRecords = debounce(DEBOUNCE_DELAY, () => {
        if (!this.entries || this.entries.length === 0) {
            return;
        }

        webapi.invoke('resource-timings/send', {reports: this.entries});
        this.entries = [];
    });

    public activate() {
        if (!("PerformanceObserver" in window)) {
            return;
        }

        this.observer = new PerformanceObserver((e => {
            const sourceEntries: PerformanceEntryList = e.getEntries();
            if (!sourceEntries || sourceEntries.length === 0) {
                return;
            }

            const currentStateId = OK.state.getCurrentStateId();
            const entries: OkPerformanceResourceTiming[] = [];
            sourceEntries.forEach((e: PerformanceEntry) => {
                if (e[DELIVERY_TYPE_FIELD as keyof PerformanceEntry] === 'cache'
                    || !(RESOURCE_NAME_PATTERN.test(e.name) && !IMAGE_NAME_PATTERN.test(e.name))) {
                    return;
                }

                entries.push(
                    convert(e as PerformanceResourceTiming, currentStateId)
                );
            });

            if (entries.length == 0) {
                return;
            }
            this.entries.push(...entries);
            this.logRecords();
        }));

        this.observer.observe({entryTypes: ['resource']});
    }

    public deactivate() {
        if (!this.observer) {
            return;
        }

        this.observer.disconnect();
    }
}

export default PerformanceTimingLogger;
